import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useAuth } from '@/layouts/RootLayout';
import { isTeamProfile } from '@/services/userPermissions';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CODE_PATTERN, CODE_PREFIX, asOrderCode } from '@/shared/orderCode';
import { useSeo } from '@/shared/seo';
import { GLOW, LABEL, WRAP } from '@/components/home/styles';
import { cn } from '@/lib/utils';
import { useT } from '@/shared/i18n';
import { C } from '@/shared/locales/checkout/keys';
import { P, around } from '@/shared/locales/pages/keys';

const { eq } = window.ApperSDK.CoreSDK;

export const route = { path: '/check-order', layout: 'public', access: 'public' };

export default function CheckOrder() {
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initial = params.get('code');
  const [code, setCode] = useState(() => (initial ? asOrderCode(initial) : CODE_PREFIX));
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const autoRan = useRef(false);
  const valid = CODE_PATTERN.test(code);
  const team = isTeamProfile(user?.profile);

  useSeo({
    title: 'Check an order',
    description: 'Type your Ghost Cable order number (GC- and six letters or numbers) to see its status, payment details and line.',
    path: '/check-order',
  });

  const loginHref = `/customer/login?redirect=${encodeURIComponent(`/check-order?code=${code}`)}`;

  async function lookUp(value) {
    if (!CODE_PATTERN.test(value)) {
      setResult({ kind: 'invalid' });
      return;
    }
    if (!user) {
      setResult({ kind: 'signin' });
      return;
    }
    setChecking(true);
    setResult(null);
    try {
      const res = await sdk.table('orders_c').select(['Name']).where(eq('Name', value)).limit(1).fetch();
      const found = res?.data?.[0];
      if (found) {
        navigate(team ? `/admin/orders/${found.Id}` : `/account/orders/${found.Id}`);
        return;
      }
      setResult({ kind: res?.success === false ? 'error' : 'missing' });
    } catch {
      setResult({ kind: 'error' });
    } finally {
      setChecking(false);
    }
  }

  useEffect(() => {
    if (autoRan.current || !initial) return;
    autoRan.current = true;
    lookUp(asOrderCode(initial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, user]);

  function submit(e) {
    e.preventDefault();
    setParams(valid ? { code } : {}, { replace: true });
    lookUp(code);
  }

  return (
    <div className="pb-24">
      <section className={cn(WRAP, 'pt-16 md:pt-24')}>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/">
            <ApperIcon name="ArrowLeft" size={16} className="rtl:rotate-180" />
            {t(P.back)}
          </Link>
        </Button>

        <div className="mx-auto mt-10 max-w-xl text-center">
          <p className={cn(LABEL, 'flex items-center justify-center gap-2')}>
            <ApperIcon name="Search" size={12} />
            {t(C.lookup)}
          </p>
          <h1 className="mt-4 text-[clamp(36px,6vw,64px)] font-light leading-[1.05]">
            {around(t(C.checkYour), 'order')[0]}
            <span className={GLOW}>{t(C.orderWord)}</span>
            {around(t(C.checkYour), 'order')[1]}
          </h1>
          <p className="mt-4 text-[17px] leading-8 text-muted-foreground">
            {around(t(C.lookupIntro), 'gc')[0]}
            <span className="font-mono text-foreground">GC-</span>
            {around(t(C.lookupIntro), 'gc')[1]}
          </p>

          <form onSubmit={submit} className="mt-8 flex flex-col gap-3 sm:flex-row">
            <label htmlFor="check-order-code" className="sr-only">
              {t(C.orderNumber)}
            </label>
            <Input
              id="check-order-code"
              value={code}
              onChange={(e) => {
                setCode(asOrderCode(e.target.value));
                setResult(null);
              }}
              onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
              placeholder="GC-7K2QXM"
              autoComplete="off"
              spellCheck={false}
              inputMode="text"
              className="h-12 flex-1 text-center font-mono text-lg uppercase tracking-[0.2em] sm:text-left"
            />
            <Button type="submit" size="lg" className="h-12" disabled={checking || !valid}>
              <ApperIcon name={checking ? 'Loader2' : 'Search'} size={16} className={checking ? 'animate-spin' : undefined} />
              {checking ? t(C.checkingL) : t(C.checkOrder)}
            </Button>
          </form>

          <div className="mt-6 min-h-24" aria-live="polite">
            {result?.kind === 'invalid' && (
              <p className="text-sm text-destructive">{t(C.invalid)}</p>
            )}
            {result?.kind === 'signin' && (
              <div className="rounded-lg border border-border bg-card px-5 py-5 text-start">
                <p className="flex items-center gap-2 font-medium">
                  <ApperIcon name="Lock" size={16} className="text-primary" />
                  {t(C.signInSee, { code })}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t(C.signInP)}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button asChild>
                    <Link to={loginHref}>{t(C.signInOpen)}</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/#contact">{t(C.contactInstead)}</Link>
                  </Button>
                </div>
              </div>
            )}
            {result?.kind === 'missing' && (
              <div className="rounded-lg border border-border bg-card px-5 py-5 text-start">
                <p className="font-medium">{t(C.noOrder, { code })}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t(C.missingP)}{' '}
                  <Link to="/#contact" className="text-primary hover:underline">
                    {t(P.sendMsg)}
                  </Link>
                  .
                </p>
              </div>
            )}
            {result?.kind === 'error' && (
              <p className="text-sm text-destructive">{t(C.lookupErr)}</p>
            )}
            {!result && (
              <p className="text-sm text-muted-foreground">
                {user ? t(C.openMine) : t(C.askSignIn)}
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
